import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { TableTemplateRecord } from "../../apiServices/tableTemplatesApi";
import { useUserStore } from "../../store/userStore";
import { useTableTemplateStore } from "../../store/tableTemplateStore";
import "./tableBuilder.css";

type TemplatePickerProps = {
  mode?: "builder" | "insert";
  onTemplateSelect?: (template: TableTemplateRecord) => void | Promise<void>;
  onClose?: () => void;
  returnTo?: string;
};

export default function TemplatePicker({
  mode = "builder",
  onTemplateSelect,
  onClose,
  returnTo,
}: TemplatePickerProps) {
  const user = useUserStore((s) => s.user);
  const navigate = useNavigate();
  const location = useLocation();

  const templates = useTableTemplateStore((s) => s.templates);
  const setOEM = useTableTemplateStore((s) => s.setOEM);
  const loadTemplates = useTableTemplateStore((s) => s.loadTemplates);
  const selectTemplate = useTableTemplateStore((s) => s.selectTemplate);
  const startNewTemplate = useTableTemplateStore((s) => s.startNewTemplate);
  const isLoading = useTableTemplateStore((s) => s.isLoading);
  const error = useTableTemplateStore((s) => s.error);

  const [search, setSearch] = useState("");
  const [scope, setScope] = useState<"all" | "mine">("all");
  const [page, setPage] = useState(1);
  const pageSize = 7;

  useEffect(() => {
    const basePath = process.env.REACT_APP_BASE_NAME || "";
    const oemFromBase = basePath.split("/")[1] || "";
    const oemFromUser = user?.oemId ? `Oem${user.oemId}` : "";
    const resolvedOEM = oemFromBase || oemFromUser || "Oem1";

    setOEM(resolvedOEM);
    void loadTemplates();
  }, [loadTemplates, setOEM, user?.oemId]);

  const username = (user?.username || "").trim().toLowerCase();

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return templates.filter((t) => {
      const isMine =
        !username || !t.createdBy
          ? true
          : t.createdBy.toLowerCase().includes(username);

      if (scope === "mine" && !isMine) return false;

      if (!query) return true;

      return (
        t.templateName.toLowerCase().includes(query) ||
        (t.displayTitle || "").toLowerCase().includes(query) ||
        String(t.tableId).includes(query) ||
        (t.createdBy || "").toLowerCase().includes(query)
      );
    });
  }, [scope, search, templates, username]);

  useEffect(() => {
    setPage(1);
  }, [scope, search, templates.length]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageSafe = Math.min(page, pageCount);
  const pageItems = filtered.slice(
    (pageSafe - 1) * pageSize,
    pageSafe * pageSize
  );

  const formatTableId = (id: number) => `TBL-${String(id).padStart(5, "0")}`;

  const formatDate = (value?: string | null) => {
    if (!value) return "--";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "--";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleCreateTemplate = () => {
    if (mode === "insert") {
      const fallbackReturnTo = `${location.pathname}${location.search}`;
      navigate("/authoring/table-template-builder", {
        state: {
          returnTo: returnTo || fallbackReturnTo,
          returnLabel: "Back to Authoring",
        },
      });
      onClose?.();
      return;
    }

    startNewTemplate();
  };

  const handleTemplateSelect = async (template: TableTemplateRecord) => {
    if (onTemplateSelect) {
      await onTemplateSelect(template);
      return;
    }

    await selectTemplate(template.tableId);
  };

  return (
    <div className="template-picker-modal">
      <div className="template-picker-head">
        <div>
          <h3 className="template-picker-head-title">Select Table Template</h3>
          <p className="template-picker-head-subtitle">
            Choose a predefined table structure to insert into your content
          </p>
        </div>
        <button
          type="button"
          className="template-picker-head-close"
          aria-label="Close picker"
          title="Close picker"
          onClick={onClose}
          disabled={!onClose}
        >
          ×
        </button>
      </div>

      <div className="template-picker-toolbar">
        <div className="template-picker-toolbar-left">
          <div className="template-picker-search-wrap">
            <span className="template-picker-search-icon">⌕</span>
            <input
              className="template-picker-search-input"
              placeholder="Search by template name, display title, table ID, author..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="template-picker-tabs">
            <button
              type="button"
              className={`template-picker-tab ${scope === "all" ? "is-active" : ""}`}
              onClick={() => setScope("all")}
            >
              All Templates
            </button>
            <button
              type="button"
              className={`template-picker-tab ${scope === "mine" ? "is-active" : ""}`}
              onClick={() => setScope("mine")}
            >
              My Templates
            </button>
          </div>
        </div>

        <button
          className="btn btn-sm btn-primary template-picker-create"
          type="button"
          onClick={handleCreateTemplate}
        >
          + Create New Template
        </button>
      </div>

      {error ? <div className="template-picker-error">{error}</div> : null}

      <div className="template-picker-table">
        <div className="template-picker-grid template-picker-grid-header">
          <div>Template Name</div>
          <div>Display Title</div>
          <div>Table ID</div>
          <div>Created By</div>
          <div>Created Date</div>
          <div className="template-picker-grid-action-col">Action</div>
        </div>

        <div className="template-picker-grid-body">
          {isLoading ? (
            <div className="template-picker-empty">Loading templates...</div>
          ) : pageItems.length === 0 ? (
            <div className="template-picker-empty">No templates found.</div>
          ) : (
            pageItems.map((t) => (
              <div
                key={t.tableId}
                className="template-picker-grid template-picker-grid-row"
                onClick={() => void handleTemplateSelect(t)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    void handleTemplateSelect(t);
                  }
                }}
              >
                <div>
                  <div className="template-picker-cell-title">{t.templateName}</div>
                </div>
                <div>
                  <div className="template-picker-cell-title">
                    {t.displayTitle || "--"}
                  </div>
                </div>
                <div className="template-picker-cell-text">{formatTableId(t.tableId)}</div>
                <div className="template-picker-cell-text">{t.createdBy || "--"}</div>
                <div className="template-picker-cell-text">
                  {formatDate(t.createdAt)}
                </div>
                <div className="template-picker-action">Select ›</div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="template-picker-foot">
        <p>
          Showing {(pageSafe - 1) * pageSize + (pageItems.length ? 1 : 0)}-
          {(pageSafe - 1) * pageSize + pageItems.length} of {filtered.length} templates
        </p>
        <div className="template-picker-pagination">
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={pageSafe === 1}
          >
            ‹
          </button>
          <span>{pageSafe}</span>
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            disabled={pageSafe === pageCount}
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
}
